import React, { useEffect, useState } from "react";
import { useRouteMatch, useLocation } from "react-router";
import { Box } from "@material-ui/core";
import _ from "lodash";

import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import withAuthority from "../../../../components/Auth/withAuthority";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import { getCookie } from "../../../../utils/cookies";
import {
  eatprestoOrderFilterObject,
  eatprestoPaymentGatewayFilterObject,
} from "../../../../utils/consts/list";
import SaleReport from "../../../../components/common/SaleReport";
import { fetchAllDailySaleReportInfoInEatpresto } from "../../../../services/eatprestoApp/dailySaleReportService";
import moment from "moment";
import DailyOrderReportTab from "./DailyOrderReportTab";
import {
  fetchLocationInfo,
  fetchPaymentGatewayStripReportInfo,
} from "../../../../services/eatprestoApp/paymentGatewayService";

export interface DailyOrderReportProps {}

/**
 * PaymentGatewayReport Component
 *
 * The PaymentGatewayReport component is responsible for displaying the payment summary report
 * for a specific location within the PrestoExpress application. It utilizes Material-UI
 * components and various utility functions to fetch and display payment-related information.
 */
const PaymentGatewayReport: React.FunctionComponent<
  DailyOrderReportProps
> = () => {
  const [paymentGatewayList, setPaymentGatewayList] = useState<any>([]);
  const [error, setError] = useState("");
  const [locationSelectorList, setLocationSelectorList] = useState([]);
  const [isGetLocationInfo, setIsGetLocationInfo] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [filterDetails, setFilterDetails] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [openFilterCard, setOpenFilterCard] = useState(false);
  const [locationSelectedList, setLocationSelectedList] = useState([]);
  const [locationInfoMap, setLocationInfoMap] = useState<any>({});
  const match: any = useRouteMatch();

  /**
   * Function Name: getDailySaleReportInfoInEatpresto
   *
   * Retrieves daily sale report information for Eatpresto and handles data processing.
   *
   */
  const getDailySaleReportInfoInEatpresto = async (filter: any) => {
    // Clear daily order report list
    setPaymentGatewayList([]);
    try {
      // Fetch daily sale report information from the server
      const response = await fetchPaymentGatewayStripReportInfo(
        match.params.locationId,
        filter,
      );
      setIsLoading(false);
      setPaymentGatewayList(response.data.data);
    } catch (err) {
      // Set loading state to false
      setIsLoading(false);
      // Set error message for unexpected error
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  const handleGetLocationInfo = async () => {
    try {
      const res = await fetchLocationInfo(match.params.locationId);
      setLocationInfoMap(res.data.data);
      setIsGetLocationInfo(true);
    } catch (error) {}
  };

  /**
   * Fetches and updates the location selector list based on the provided search name.
   *
   * @param {string} searchName - The search name used for filtering locations.
   */
  const getAllFilterLocation = async (searchName: any) => {
    try {
      // Fetch all filter locations using the provided search name
      const response = await fetchAllFilterLocations(searchName);

      // Process the fetched data to create a list of
      if (!_.isEmpty(response.data.data)) {
        const locationList = response.data.data.map((location: any) => ({
          id: location.id,
          label: location.businessDisplayName,
        }));

        // Update the location selector list and set the flag indicating location information is retrieved
        setLocationSelectorList(locationList);
      } else {
        setLocationSelectorList([]);
      }
      handleGetLocationInfo();
    } catch (error) {
      // Handle errors by setting an error message
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /**
   * useEffect hook to set the document title and fetch initial location filter data.
   * It runs only once when the component mounts.
   */
  useEffect(() => {
    // Set the document title for the page
    document.title = "Eatpresto - Payments Report";

    // Fetch initial location filter data with an empty searchName
    getAllFilterLocation("");
  }, []);

  /**
   * Handles the filter data and triggers the update of the payment report.
   */
  const handleFilterData = (filterData: any) => {
    setIsLoading(true);
    const filter = {
      startDate: moment(filterData.orderTimeStart).format("YYYY-MM-DD"),
      endDate: moment(filterData.orderTimeEnd).format("YYYY-MM-DD"),
    };
    // Fetch and update the payment report information based on the provided filter data and shift flag
    getDailySaleReportInfoInEatpresto(filter);
  };

  /**
   * Handles the click event on the text, opens the filter card, and scrolls to the top.
   */
  const handleOnClickText = () => {
    setOpenFilterCard(true);

    // Scroll to the top of the window with smooth behavior
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*Get location list API call after typing. */
  const handleLocationSelectorTypingList = (searchName: any) => {};
  return (
    <>
      <Box>
        {locationInfoMap.isPaymentsReportEnabled && (
          <SaleReport
            handleFilterData={handleFilterData}
            locationSelectorList={locationSelectorList}
            handleLocationSelectorTypingList={handleLocationSelectorTypingList}
            isGetLocationInfo={isGetLocationInfo}
            setFilterDetails={setFilterDetails}
            availableFilter={eatprestoPaymentGatewayFilterObject}
            isOpenSkeletonLoading={isLoading}
            nodeList={paymentGatewayList}
            filterDetails={filterDetails}
            title={""}
            isChart={false}
            filterTitle="Payment Gateway report for"
            topic="Payments Report"
            isNeedFilterDetails={true}
            locationSelectedList={locationSelectedList}
            setLocationSelectedList={setLocationSelectedList}
            openFilterCard={openFilterCard}
            setOpenFilterCard={setOpenFilterCard}
            handleOnClickText={handleOnClickText}
            isReport={true}
            payInOut={false}
            isInitialLoad={isInitialLoad}
            setIsInitialLoad={setIsInitialLoad}
            initialParamSize={5}
            isOrderReport={true}
            isWeekDateRange={true}
          >
            <DailyOrderReportTab
              nodeList={paymentGatewayList}
              filterDetails={filterDetails}
              locationSelectedList={locationSelectedList}
              handleOnClickText={handleOnClickText}
              locationInfoMap={locationInfoMap}
            />
          </SaleReport>
        )}

        <DefaultAlert
          open={!!error}
          handleClose={() => setError("")}
          message={error}
          severity="error"
        />
      </Box>
    </>
  );
};

export default withAuthority(PaymentGatewayReport, Authorities.DASHBOARD_READ);
